import { Button, Card, Popconfirm, Tag } from "@arco-design/web-react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { dicMajorPageSizeOptions } from "../config";
import type { DicMajorAliasItem, DicMajorItem } from "../types";
import "./DicMajorPage.css";

type DicMajorResultTableProps = {
  canManage: boolean;
  items: DicMajorItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onCreateMajor: () => void;
  onEditMajor: (major: DicMajorItem) => void;
  onCreateAlias: (major: DicMajorItem) => void;
  onEditAlias: (major: DicMajorItem, alias: DicMajorAliasItem) => void;
  onDeleteAlias: (major: DicMajorItem, alias: DicMajorAliasItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

/** 标准专业结果表格。 */
export function DicMajorResultTable({
  canManage,
  items,
  loading,
  page,
  pageSize,
  total,
  onCreateMajor,
  onEditMajor,
  onCreateAlias,
  onEditAlias,
  onDeleteAlias,
  onPageChange,
  onPageSizeChange,
}: DicMajorResultTableProps) {
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <Card className="dic-major-card" bodyStyle={{ padding: 0 }}>
      <div className="dic-major-results">
        <div className="dic-major-results-header">
          <h2>标准专业字典</h2>
          <Button shape="round" icon={<Plus size={16} />} disabled={!canManage} onClick={onCreateMajor}>
            新增专业
          </Button>
        </div>
        <div className="dic-major-table-scroll">
          <table className="dic-major-table">
            <thead>
              <tr>
                <th>操作</th>
                <th>标准专业</th>
                <th>专业大类</th>
                <th>拼音码</th>
                <th>排序</th>
                <th>状态</th>
                <th>专业别名</th>
                <th>更新时间</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="dic-major-empty">
                    正在加载标准专业
                  </td>
                </tr>
              ) : null}
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="dic-major-empty">
                    暂无标准专业数据
                  </td>
                </tr>
              ) : null}
              {!loading
                ? items.map((item) => (
                    <tr key={item.majorId}>
                      <td>
                        <div className="dic-major-row-actions">
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`编辑专业 ${item.majorName}`}
                            title="编辑专业"
                            icon={<Pencil size={16} />}
                            disabled={!canManage}
                            onClick={() => onEditMajor(item)}
                          />
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`新增别名 ${item.majorName}`}
                            title="新增别名"
                            icon={<Plus size={16} />}
                            disabled={!canManage}
                            onClick={() => onCreateAlias(item)}
                          />
                        </div>
                      </td>
                      <td className="dic-major-name">{item.majorName}</td>
                      <td>{item.majorCategory}</td>
                      <td>{item.spellCode}</td>
                      <td>{item.sortNo}</td>
                      <td>
                        <Tag color={item.isDisabled ? "orangered" : "green"}>
                          {item.isDisabledLabel}
                        </Tag>
                      </td>
                      <td>
                        <div className="dic-major-alias-list">
                          {item.aliases.map((alias) => (
                            <span className="dic-major-alias-chip" key={alias.majorAliasId}>
                              <span>{alias.aliasName}</span>
                              <Button
                                type="text"
                                shape="circle"
                                aria-label={`编辑别名 ${alias.aliasName}`}
                                title="编辑别名"
                                icon={<Pencil size={14} />}
                                disabled={!canManage}
                                onClick={() => onEditAlias(item, alias)}
                              />
                              <Popconfirm
                                title="确认删除该别名？"
                                content="删除后该别名不再参与标准专业匹配。"
                                okText="确认删除"
                                cancelText="取消"
                                onOk={() => onDeleteAlias(item, alias)}
                                trigger="click"
                                unmountOnExit
                              >
                                <Button
                                  type="text"
                                  shape="circle"
                                  status="danger"
                                  aria-label={`删除别名 ${alias.aliasName}`}
                                  title="删除别名"
                                  icon={<Trash2 size={14} />}
                                  disabled={!canManage}
                                />
                              </Popconfirm>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>{item.updatedAt}</td>
                      <td>{item.remark}</td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
        <div className="dic-major-pagination">
          <span>共 {total} 条</span>
          <Button disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            上一页
          </Button>
          <span>
            {page} / {totalPages}
          </span>
          <Button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            下一页
          </Button>
          <label className="dic-major-page-size">
            <span>每页</span>
            <select
              aria-label="每页条数"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {dicMajorPageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option} 条
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </Card>
  );
}

